import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  IdentityLevel,
  IdentityScreenAction,
  IdentityScreenProcess,
  isIdentityLevel,
  isIdentityScreenAction,
  isIdentityScreenProcess,
} from '../types/registrationFlows';
import { BrandGroupConfig } from '../types/apiConfig';
import { brandGroupConfigSelector } from '../redux/reducers/brandGroupConfigReducer';
import { apiConfigSelector } from '../redux/reducers/apiConfigReducer';
import { returnUrlPolicy, toRegistrationRedirectUri } from '../utils/returnUrl';

export type RegistrationFlowConfiguration = {
  identityLevel: IdentityLevel;
  skipEmailVerification: boolean;
  brand: string;
  action: IdentityScreenAction;
  process: IdentityScreenProcess;
  redirectURI: string;
  flowId: string;
};

export enum RegistrationFlowParams {
  IDENTITY_LEVEL = 'identity_level',
  SKIP_EMAIL_VERIFICATION = 'skip_email_verification',
  BRAND = 'brand',
  ACTION = 'action',
  PROCESS = 'process',
  REDIRECT_URI = 'redirect_uri',
  REGISTRATION_TYPE = 'registration_type',
  FLOW_ID = 'flowId',
}

// The flow continues either through the hosted flow (flowId) or through redirect_uri, so it needs
// one of the two rather than redirect_uri specifically.
const requiredRegistrationParams: (keyof RegistrationFlowConfiguration)[] = [
  'identityLevel',
  'action',
  'skipEmailVerification',
  'process',
];

export function hasRequiredRegistrationFlowParams(
  config: Partial<RegistrationFlowConfiguration>
): config is RegistrationFlowConfiguration {
  return (
    requiredRegistrationParams.every((key) => config[key] !== undefined) &&
    (config.flowId !== undefined || config.redirectURI !== undefined)
  );
}

export function useRegistrationFlowConfiguration(): Partial<RegistrationFlowConfiguration> {
  const [searchParams] = useSearchParams();
  const brandGroups: BrandGroupConfig[] = useSelector(brandGroupConfigSelector);
  const { devReturnUrlDomains } = useSelector(apiConfigSelector);
  const registrationConfig: Partial<RegistrationFlowConfiguration> = useMemo(() => {
    const rawRegistrationParams = Object.fromEntries(searchParams.entries());
    let action = isIdentityScreenAction(rawRegistrationParams[RegistrationFlowParams.ACTION])
      ? rawRegistrationParams[RegistrationFlowParams.ACTION]
      : undefined;
    if (action === IdentityScreenAction.LOGIN) {
      action = IdentityScreenAction.UPGRADE;
    }
    const skipEmailVerification =
      action === IdentityScreenAction.UPGRADE
        ? true
        : rawRegistrationParams[RegistrationFlowParams.SKIP_EMAIL_VERIFICATION] === '1';
    return {
      identityLevel: isIdentityLevel(rawRegistrationParams[RegistrationFlowParams.IDENTITY_LEVEL])
        ? rawRegistrationParams[RegistrationFlowParams.IDENTITY_LEVEL]
        : undefined,
      skipEmailVerification,
      brand: rawRegistrationParams[RegistrationFlowParams.BRAND],
      action,
      process: isIdentityScreenProcess(rawRegistrationParams[RegistrationFlowParams.PROCESS])
        ? rawRegistrationParams[RegistrationFlowParams.PROCESS]
        : IdentityScreenProcess.OTHER,
      redirectURI: toRegistrationRedirectUri(
        rawRegistrationParams[RegistrationFlowParams.REDIRECT_URI],
        returnUrlPolicy(brandGroups, devReturnUrlDomains)
      ),
      flowId: rawRegistrationParams[RegistrationFlowParams.FLOW_ID],
    };
  }, [searchParams, brandGroups, devReturnUrlDomains]);

  return registrationConfig;
}
